import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const boxRef = useRef(null);

  const logged = Boolean(user);

  useEffect(() => {
    if (!logged) return;
    load();
    const timer = setInterval(load, 120000);
    return () => clearInterval(timer);
  }, [logged]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const load = async () => {
    try {
      const res = await api.get('/insurance/notifications');
      setItems(res.data?.items || []);
      setUnread(res.data?.unread || 0);
    } catch (e) { /* silent */ }
  };

  const markRead = async () => {
    try {
      await api.post('/insurance/notifications/read');
      setUnread(0);
      setItems((list) => list.map((i) => ({ ...i, read: true })));
    } catch (e) { /* silent */ }
  };

  if (!logged) return null;

  return (
    <div className="relative" ref={boxRef}>
      <button
        onClick={() => { setOpen((v) => !v); if (!open && unread > 0) markRead(); }}
        className="relative flex h-11 w-11 items-center justify-center rounded-xl transition-colors hover:bg-gray-100"
        data-testid="notification-bell"
        aria-label="Notificări"
      >
        <Bell className="h-5 w-5 text-gray-700" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white" data-testid="notification-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 rounded-2xl border border-gray-100 bg-white shadow-xl" data-testid="notification-panel">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <p className="text-sm font-semibold text-gray-900">Notificări</p>
            {items.length > 0 && (
              <button onClick={markRead} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900" data-testid="notification-mark-read">
                <CheckCheck className="h-3.5 w-3.5" /> Marchează citite
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 && <p className="px-4 py-8 text-center text-sm text-gray-500">Nicio notificare</p>}
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => { setOpen(false); if (n.link) navigate(n.link); }}
                className={`block w-full border-b border-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-50 ${n.read ? '' : 'bg-green-50/40'}`}
                data-testid={`notification-item-${n.id}`}
              >
                <p className="text-sm font-medium text-gray-900">{n.title}</p>
                <p className="mt-0.5 text-xs text-gray-500">{n.message}</p>
                <p className="mt-1 text-[11px] text-gray-400">{String(n.createdAt || '').slice(0, 10)}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
